import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Car, Calendar, Gauge } from 'lucide-react';
import { Button } from './ui/button';
import { vehiclesAPI } from '../services/api';
import { ga } from '../services/ga';
import { vehicleStore } from '../services/vehicleStore';
import GarageQuickPick from './GarageQuickPick';
import { useT } from '../services/i18n';
import VehicleDropdown from './vehicle-selector/VehicleDropdown';
import { BrandOptions, ModelOptions, YearOptions, EngineOptions } from './vehicle-selector/DropdownOptions';
import { PlateSearch, SelectedVehicleSummary } from './vehicle-selector/PlateSearch';
import { useVehicleData } from './vehicle-selector/useVehicleData';

const VehicleSelector = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const saved = useMemo(() => vehicleStore.get(), []);
  const [selectedBrand, setSelectedBrand] = useState(saved?.brand || '');
  const [selectedModel, setSelectedModel] = useState(saved?.model || '');
  const [selectedYear, setSelectedYear] = useState(saved?.year ? String(saved.year) : '');
  const [selectedEngine, setSelectedEngine] = useState(saved?.engine || null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [brandSearch, setBrandSearch] = useState('');
  const [modelSearch, setModelSearch] = useState('');
  const [plateResult, setPlateResult] = useState(null);
  const data = useVehicleData(selectedBrand, selectedModel, selectedYear);

  const handleSearch = () => {
    if (!selectedEngine) return;
    vehicleStore.set({ brand: selectedBrand, model: selectedModel, year: parseInt(selectedYear), engine: selectedEngine });
    ga.search(`${selectedBrand} ${selectedModel} ${selectedEngine}`, 'vehicle', { search_results: 1, vehicle_brand: selectedBrand, vehicle_model: selectedModel, vehicle_year: selectedYear, vehicle_engine: selectedEngine });
    navigate('/pieces');
  };

  const handleBrandSelect = useCallback((brand) => {
    setSelectedBrand(brand); setSelectedModel(''); setSelectedYear(''); setSelectedEngine(null);
    setOpenDropdown(null); setBrandSearch(''); setPlateResult(null);
  }, []);

  // Model without picture → ask the backend to generate one (AI) and refresh the thumbnails while it is being produced
  const imagePollRef = useRef(null);
  const { setModelImages } = data;
  const requestModelImage = useCallback((brand, model) => {
    if (!brand || !model) return;
    clearInterval(imagePollRef.current);
    vehiclesAPI.requestModelImage(brand, model).then((r) => {
      if (r?.image) { setModelImages(prev => ({ ...prev, [model]: r.image })); return; }
      if (!r?.queued && r?.status !== 'queued' && r?.status !== 'running') return;
      let tries = 0;
      imagePollRef.current = setInterval(async () => {
        tries += 1;
        try {
          const st = await vehiclesAPI.modelImageStatus(brand, model);
          if (st.image) { setModelImages(prev => ({ ...prev, [model]: st.image })); clearInterval(imagePollRef.current); }
          else if (st.status === 'failed' || tries >= 18) clearInterval(imagePollRef.current);
        } catch { clearInterval(imagePollRef.current); }
      }, 5000);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => clearInterval(imagePollRef.current), []);

  const handleModelSelect = (model) => {
    setSelectedModel(model); setSelectedYear(''); setSelectedEngine(null); setOpenDropdown(null); setModelSearch('');
    if (!data.modelImages[model]) requestModelImage(selectedBrand, model);
  };
  const handleYearSelect = (year) => { setSelectedYear(year); setSelectedEngine(null); setOpenDropdown(null); };
  const handleEngineSelect = (engine) => { setSelectedEngine(engine); setOpenDropdown(null); };
  const clearBrand = () => { setSelectedBrand(''); setSelectedModel(''); setSelectedYear(''); setSelectedEngine(null); };
  const clearModel = () => { setSelectedModel(''); setSelectedYear(''); setSelectedEngine(null); };
  const clearYear = () => { setSelectedYear(''); setSelectedEngine(null); };
  const toggle = (id) => setOpenDropdown((cur) => (cur === id ? null : id));

  useEffect(() => {
    const handleClickOutside = (e) => { if (!e.target.closest('.dropdown-container')) setOpenDropdown(null); };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const onPlateIdentified = (r) => { setSelectedBrand(r.brand); setSelectedModel(r.model); setSelectedYear(String(r.year)); setSelectedEngine(r.engine); };

  return (
    <div className="bg-gray-100 pt-4 pb-6 scroll-mt-20" id="selecteur-vehicule">
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <p className="text-sm font-semibold text-gray-800 mb-3" data-testid="vehicle-selector-title">{t('home.selectVehicle')}</p>
          <GarageQuickPick onPick={() => navigate('/pieces')} testId="home-garage-pick" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <VehicleDropdown id="brand" label={t('home.brand')} value={selectedBrand} displayValue={selectedBrand} logo={data.brandLogos[selectedBrand]} placeholder={t('home.chooseBrand')} disabled={false} loading={data.loadingBrands} onClear={clearBrand} icon={Car} open={openDropdown === 'brand'} onToggle={toggle}>
              <BrandOptions brands={data.brands} brandLogos={data.brandLogos} popularBrands={data.popularBrands} selected={selectedBrand} search={brandSearch} setSearch={setBrandSearch} onSelect={handleBrandSelect} />
            </VehicleDropdown>

            <VehicleDropdown id="model" label={t('home.model')} value={selectedModel} displayValue={selectedModel} placeholder={t('home.chooseModel')} disabled={!selectedBrand} loading={data.loadingModels} onClear={clearModel} icon={Car} logo={selectedModel && data.modelImages[selectedModel] ? data.modelImages[selectedModel] : null} logoClass="w-10 h-7" open={openDropdown === 'model'} onToggle={toggle}>
              {selectedBrand && <ModelOptions brand={selectedBrand} models={data.models} modelImages={data.modelImages} selected={selectedModel} search={modelSearch} setSearch={setModelSearch} onSelect={handleModelSelect} />}
            </VehicleDropdown>

            <VehicleDropdown id="year" label={t('home.year')} value={selectedYear} displayValue={selectedYear} placeholder={t('home.chooseYear')} disabled={!selectedModel} loading={data.loadingYears} onClear={clearYear} icon={Calendar} image="/images/selector/year.png" open={openDropdown === 'year'} onToggle={toggle}>
              <YearOptions years={data.years} selected={selectedYear} onSelect={handleYearSelect} />
            </VehicleDropdown>

            <VehicleDropdown id="engine" label={t('home.engine')} value={selectedEngine} displayValue={selectedEngine ? `${selectedEngine.name} (${selectedEngine.power}ch)` : ''} placeholder={t('home.chooseEngine')} disabled={!selectedYear} loading={data.loadingEngines} onClear={() => setSelectedEngine(null)} icon={Gauge} image="/images/selector/engine.png" open={openDropdown === 'engine'} onToggle={toggle}>
              <EngineOptions engines={data.engines} selectedYear={selectedYear} selected={selectedEngine} onSelect={handleEngineSelect} />
            </VehicleDropdown>

            <div className="flex items-end">
              <Button data-testid="vehicle-search-button" onClick={handleSearch} className="w-full bg-orange-500 hover:bg-orange-600 py-3 rounded-lg h-[50px] disabled:opacity-50 disabled:cursor-not-allowed" disabled={!selectedEngine}>
                <Search size={20} className="mr-2" />
                {t('home.searchBtn')}
              </Button>
            </div>
          </div>

          {selectedEngine && <SelectedVehicleSummary brand={selectedBrand} model={selectedModel} year={selectedYear} engine={selectedEngine} />}

          <PlateSearch result={plateResult} setResult={setPlateResult} onIdentified={onPlateIdentified} />

          <div className="mt-4 text-center text-sm">
            <Link to="/vehicules" className="text-blue-600 hover:text-blue-700 hover:underline" data-testid="vehicle-brands-link">{t('home.allVehicleBrands')}</Link>
            <span className="mx-2 text-gray-300">·</span>
            <Link to="/contact" className="text-blue-600 hover:text-blue-700 hover:underline">{t('home.notFoundCar')}</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleSelector;
